import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  X, DownloadSimple, ArrowSquareOut, ArrowClockwise, FileText,
} from '@phosphor-icons/react';
import { dmsApi } from './dmsApi';
import { drawingRevApi } from './drawingRevApi';
import { OfficePreview } from './OfficePreview';
import { ZoomPanImage } from './ZoomPanImage';

const IMG_EXT = ['png', 'jpg', 'jpeg', 'webp', 'gif', 'tif', 'tiff', 'bmp'];
const OFFICE_EXT = ['doc', 'docx', 'xls', 'xlsx', 'xlsm', 'xlsb', 'ppt', 'pptx',
  'odt', 'ods', 'odp', 'csv'];

export const viewerKind = (filename = '', mime = '') => {
  const e = (filename.split('.').pop() || '').toLowerCase();
  if (mime === 'application/pdf' || e === 'pdf') return 'pdf';
  if (mime.startsWith('image/') || IMG_EXT.includes(e)) return 'image';
  if (OFFICE_EXT.includes(e)) return 'office';
  return 'other';
};

/** サーバーやCDNの生のエラー本文（HTML）をそのまま出さない。 */
const readableError = (e) => {
  const raw = e?.detail || e?.message || '';
  const status = e?.status ?? e?.response?.status ?? null;
  if (!raw || raw.length > 120 || /<\/?[a-z]/i.test(raw)) {
    return status
      ? `ファイルを読み出せませんでした（サーバー応答 ${status}）`
      : 'ファイルを読み出せませんでした';
  }
  return raw;
};

/**
 * 1つの書類を全画面でちゃんと見るビューアー。
 * PDFはブラウザのPDF表示（巻物のようにスクロール・検索・印刷ができる）、
 * 画像は拡大・移動できるビューアー、Office は PDF 変換を通す。
 * 原本が取れないときは、高解像度のページ画像に落として見せる。
 */
export function DocFileViewer({ documentId, filename, title, mime = '', page,
                               thumbUrl, onClose }) {
  const kind = viewerKind(filename || '', mime || '');
  const [fileUrl, setFileUrl] = useState(null);
  const [hires, setHires] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      // 背後のドロワー（Radix Dialog）にも Esc が届くと、ビューアーと一緒に閉じてしまう。
      e.stopPropagation();
      e.preventDefault();
      onClose?.();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  useEffect(() => {
    let alive = true;
    const made = [];
    setFileUrl(null); setHires(null); setErr(null);
    if (!documentId || kind === 'office' || kind === 'other') return () => {};
    (async () => {
      try {
        const { url } = await dmsApi.fileBlobUrl(documentId);
        made.push(url);
        if (alive) setFileUrl(url); else URL.revokeObjectURL(url);
      } catch {
        // 原本が読めない（保護・変換が要る形式など）→ 高精細のページ画像で代替
        try {
          const u = await drawingRevApi.pageImage(documentId, 3, { maxEdge: 4000 });
          made.push(u);
          if (alive) setHires(u); else URL.revokeObjectURL(u);
        } catch (e2) {
          if (alive) setErr(readableError(e2));
        }
      }
    })();
    return () => { alive = false; made.forEach((u) => URL.revokeObjectURL(u)); };
  }, [documentId, kind]);

  const openTab = async () => {
    try {
      const r = await dmsApi.fileBlobUrl(documentId);
      window.open(r.url, '_blank', 'noopener');
    } catch { toast.error('ファイルを開けませんでした'); }
  };

  const body = () => {
    if (err) {
      return (
        <div className="flex h-full flex-col items-center justify-center gap-2 text-zinc-300">
          <FileText size={40} weight="duotone" />
          <p className="text-sm" data-testid="doc-viewer-error">{err}</p>
          <button type="button" onClick={openTab}
            className="rounded-md border border-white/30 px-3 py-1 text-xs hover:bg-white/10">
            新しいタブで開く
          </button>
        </div>
      );
    }
    if (kind === 'office' || kind === 'other') {
      return (
        <OfficePreview docId={documentId} doc={{ current_filename: filename }}
          dark heightClass="h-full" />
      );
    }
    if (kind === 'pdf' && fileUrl) {
      return (
        <iframe title={title || filename}
          src={`${fileUrl}#page=${page || 1}&zoom=page-fit`}
          className="h-full w-full rounded-lg bg-white"
          data-testid="doc-viewer-pdf" />
      );
    }
    if (fileUrl || hires) {
      return <ZoomPanImage src={fileUrl || hires} alt={title || filename}
        testId="doc-viewer-image" />;
    }
    return (
      <div className="relative flex h-full items-center justify-center overflow-hidden">
        {thumbUrl && (
          <img src={thumbUrl} alt="" className="max-h-full max-w-full opacity-40 blur-sm" />
        )}
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-zinc-300">
          <ArrowClockwise size={28} className="animate-spin" />
          <p className="text-xs" data-testid="doc-viewer-loading">鮮明な画像を読み込んでいます…</p>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[120] flex flex-col bg-zinc-900/95 backdrop-blur-sm"
      role="dialog" aria-modal="true" data-testid="doc-file-viewer">
      <div className="flex shrink-0 items-center gap-3 border-b border-white/10 px-4 py-2.5 text-white">
        <FileText size={18} weight="duotone" className="shrink-0 text-teal-400" />
        <span className="truncate text-sm font-semibold" data-testid="doc-viewer-title">
          {title || filename}
        </span>
        <span className="truncate text-xs text-zinc-400">{filename}</span>
        <button type="button" onClick={openTab}
          className="ml-auto inline-flex shrink-0 items-center gap-1 rounded-md border border-white/25 px-2.5 py-1 text-xs hover:bg-white/10"
          data-testid="doc-viewer-open-tab"><ArrowSquareOut size={13} /> 新しいタブで開く</button>
        <button type="button" onClick={() => dmsApi.downloadFile(documentId, null, filename)}
          className="inline-flex shrink-0 items-center gap-1 rounded-md border border-white/25 px-2.5 py-1 text-xs hover:bg-white/10"
          data-testid="doc-viewer-download"><DownloadSimple size={13} /> ダウンロード</button>
        <button type="button" onClick={onClose} aria-label="閉じる"
          className="shrink-0 rounded-md border border-white/25 p-1 hover:bg-white/10"
          data-testid="doc-viewer-close"><X size={14} /></button>
      </div>
      <div className="min-h-0 flex-1 p-3">{body()}</div>
    </div>
  );
}

export default DocFileViewer;
